import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import MaintenanceInfaqModal from '../../components/provider-dashboard/MaintenanceInfaqModal'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'

interface VehicleAsset {
  id: string
  name: string
  type: 'AMBULANCE_VEHICLE' | 'HEARSE' | 'TRANSPORT_VEHICLE'
  status: 'ACTIVE' | 'MAINTENANCE' | 'RETIRED' | 'DAMAGED'
  description?: string
  location?: string
  createdAt: string
  updatedAt: string
  images?: string[]

  // Vehicle specific fields
  licensePlate?: string
  vin?: string
  make?: string
  model?: string
  year?: number
  color?: string
  dailyCost?: number
}

const AmbulanceAssetManagementPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { assetId } = useParams<{ assetId?: string }>()
  const [assets, setAssets] = useState<VehicleAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<VehicleAsset | null>(null)
  const [formData, setFormData] = useState<Partial<VehicleAsset>>({})
  const [saving, setSaving] = useState(false)
  const [showInfaqModal, setShowInfaqModal] = useState(false)

  // Helper function to format asset type for display
  const formatAssetType = (type: string) => {
    if (!type) return 'Vehicle'

    // Import ASSET_TYPES to get proper labels
    const ASSET_TYPES = [
      { value: 'AMBULANCE_VEHICLE', label: 'Ambulance Vehicle' },
      { value: 'HEARSE', label: 'Mobil Jenazah' },
      { value: 'TRANSPORT_VEHICLE', label: 'Transport Vehicle' },
    ]

    const assetType = ASSET_TYPES.find(at => at.value === type)
    return assetType ? assetType.label : type.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
  }

  const vehicleTypes = [
    'AMBULANCE_VEHICLE',
    'HEARSE',
    'TRANSPORT_VEHICLE'
  ]

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    MAINTENANCE: 'bg-yellow-100 text-yellow-800',
    RETIRED: 'bg-gray-100 text-gray-800',
    DAMAGED: 'bg-red-100 text-red-800'
  }

  const statusIcons = {
    ACTIVE: CheckCircleIcon,
    MAINTENANCE: ClockIcon,
    RETIRED: XCircleIcon,
    DAMAGED: XCircleIcon
  }

  useEffect(() => {
    if (assetId && assetId !== 'new') {
      loadAsset(assetId)
    } else {
      setLoading(false)
    }
  }, [assetId])

  const loadAsset = async (id: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/provider-dashboard/assets/vehicles/${id}`)
      setEditingAsset(response.data)
      setFormData(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load asset')
      navigate('/provider-dashboard/assets')
    } finally {
      setLoading(false)
    }
  }

  const fetchAssets = async () => {
    try {
      const response = await api.get('/provider-dashboard/assets/vehicles')
      setAssets(response.data)
    } catch (err: any) {
      console.log('Error fetching assets:', err.message || err)
      setAssets([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!assetId) {
      fetchAssets()
    }
  }, [assetId])

  const handleAddAsset = () => {
    setEditingAsset(null)
    setFormData({
      name: '',
      type: 'AMBULANCE_VEHICLE',
      status: 'ACTIVE',
      description: '',
      location: '',
      dailyCost: 0,
    })
    navigate('/provider-dashboard/assets/new')
  }

  const handleEditAsset = (asset: VehicleAsset) => {
    navigate(`/provider-dashboard/assets/${asset.id}`)
  }

  const handleBackToList = () => {
    navigate('/provider-dashboard/assets')
    setEditingAsset(null)
    setFormData({})
  }

  const handleSaveAsset = async () => {
    setSaving(true)
    try {
      let response

      const previousStatus = editingAsset?.status
      const transitioningToMaintenance =
        formData.status === 'MAINTENANCE' && previousStatus !== 'MAINTENANCE'

      if (editingAsset) {
        response = await api.put(`/provider-dashboard/assets/vehicles/${editingAsset.id}`, formData)
        toast.success('Vehicle updated successfully')
      } else {
        response = await api.post('/provider-dashboard/assets/vehicles', formData)
        toast.success('Vehicle added successfully')
      }

      const savedAsset = response.data
      // Keep editingAsset in state so the infaq modal can target it
      if (savedAsset?.id) {
        setEditingAsset(savedAsset)
      }

      if (transitioningToMaintenance && savedAsset?.id) {
        // Hold off navigating back; let the provider decide whether to
        // open a donation campaign via the modal.
        setShowInfaqModal(true)
        return
      }

      if (!assetId || assetId === 'new') {
        fetchAssets()
      }

      handleBackToList()
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save vehicle')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAsset = async (assetId: string) => {
    if (!confirm('Are you sure you want to delete this vehicle?')) return

    try {
      await api.delete(`/provider-dashboard/assets/vehicles/${assetId}`)
      setAssets(assets.filter(a => a.id !== assetId))
      toast.success('Vehicle deleted successfully')
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to delete vehicle')
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  // Show edit/create form
  if (assetId) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToList}
                className="text-gray-600 hover:text-gray-900 flex items-center"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Vehicles
              </button>
              <div className="bg-blue-100 p-3 rounded-full">
                <TruckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {editingAsset ? 'Edit Vehicle' : 'Add New Vehicle'}
                </h1>
                <p className="text-gray-600">Manage your ambulance and transport vehicles</p>
              </div>
            </div>
          </div>
        </div>

        {/* Vehicle Form */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Name *
                </label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Type *
                </label>
                <select
                  value={formData.type || ''}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {vehicleTypes.map(type => (
                    <option key={type} value={type}>
                      {formatAssetType(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  value={formData.status || ''}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {Object.keys(statusColors).map(status => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Daily Cost (RM)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.dailyCost || ''}
                  onChange={(e) => handleInputChange('dailyCost', parseFloat(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <h4 className="text-sm font-medium text-gray-900 mb-3 border-t border-gray-200 pt-4">
              Vehicle Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  value={formData.licensePlate || ''}
                  onChange={(e) => handleInputChange('licensePlate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., ABC 1234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VIN
                </label>
                <input
                  type="text"
                  value={formData.vin || ''}
                  onChange={(e) => handleInputChange('vin', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Vehicle identification number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make
                </label>
                <input
                  type="text"
                  value={formData.make || ''}
                  onChange={(e) => handleInputChange('make', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Toyota"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  value={formData.model || ''}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., HiAce Ambulance"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <input
                  type="number"
                  value={formData.year || ''}
                  onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 2022"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="text"
                  value={formData.color || ''}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., White"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Kuala Lumpur, Malaysia"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={handleBackToList}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAsset}
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (
                <LoadingSpinner size="sm" />
              ) : (
                editingAsset ? 'Update Vehicle' : 'Add Vehicle'
              )}
            </button>
          </div>
        </div>

        {showInfaqModal && editingAsset?.id && (
          <MaintenanceInfaqModal
            assetId={editingAsset.id}
            assetName={editingAsset.name || formData.name || 'Vehicle'}
            onClose={() => {
              setShowInfaqModal(false)
              handleBackToList()
            }}
            onDone={(created) => {
              setShowInfaqModal(false)
              if (created) {
                navigate('/provider-dashboard/infaq')
              } else {
                handleBackToList()
              }
            }}
          />
        )}
      </div>
    )
  }

  // Show list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <TruckIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Vehicle Management</h1>
              <p className="text-gray-600">Manage your ambulance and transport vehicles</p>
            </div>
          </div>
          <button
            onClick={handleAddAsset}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Vehicle</span>
          </button>
        </div>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assets.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first vehicle.</p>
            <div className="mt-6">
              <button
                onClick={handleAddAsset}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Vehicle
              </button>
            </div>
          </div>
        ) : (
          assets.map((vehicle) => {
            const StatusIcon = statusIcons[vehicle.status as keyof typeof statusIcons]

            return (
              <div key={vehicle.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <TruckIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{vehicle.name}</h3>
                      <p className="text-sm text-gray-500">{formatAssetType(vehicle.type)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[vehicle.status as keyof typeof statusColors]}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {vehicle.status}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {vehicle.licensePlate && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">License:</span>
                      <span className="ml-2">{vehicle.licensePlate}</span>
                    </div>
                  )}
                  {(vehicle.make && vehicle.model) && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Model:</span>
                      <span className="ml-2">{vehicle.make} {vehicle.model}</span>
                    </div>
                  )}
                  {vehicle.year && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Year:</span>
                      <span className="ml-2">{vehicle.year}</span>
                    </div>
                  )}
                  {vehicle.dailyCost && (
                    <div className="flex items-center text-sm text-gray-600">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Daily Cost:</span>
                      <span className="ml-2">RM{vehicle.dailyCost}</span>
                    </div>
                  )}
                  {vehicle.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Location:</span>
                      <span className="ml-2">{vehicle.location}</span>
                    </div>
                  )}
                </div>

                {vehicle.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">{vehicle.description}</p>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditAsset(vehicle)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAsset(vehicle.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default AmbulanceAssetManagementPage